/**
 * OPENING HOSTING round (2026-09-30, Matt on Foulger: "the doors and windows seem to
 * always default to wanting to be placed in the wall below the level of the view I am
 * working in … when I try to change its level to bring it up, it goes up, but doesn't
 * cut in the wall at the level above … add Revit's 'pick new host'").
 *
 * Three Revit rules, one module:
 *  - An opening belongs to the level you are WORKING on; its host is a wall that stands
 *    AT that level — the level's own walls, or a taller wall rising through it
 *    (`wallReachesLevel`, shared with Paste Aligned).
 *  - Changing an opening's level when its current wall doesn't reach the new level
 *    re-hosts it into the wall at the same spot on that level (`setOpeningLevel`) —
 *    the move Revit itself only allows through Pick New Host.
 *  - Pick New Host (`pickNewHost`): the same opening, every setting kept, moved into a
 *    different wall at a station; its level follows the host (or the working level
 *    when the host rises through it).
 */
import type { ElementId, LevelId, ParamValue, Path2D, Pt2 } from '../types'
import { getElement, getLevel, getType, listElements, listLevels, maps, transact, type ModelDoc } from '../doc'
import { flattenPathPts } from '../arc'
import { polylinePointAt, projectToPolyline } from '../pathOps'
import type * as Y from 'yjs'

const isPath = (p: unknown): p is Path2D =>
  !!p && typeof p === 'object' && Array.isArray((p as Path2D).nodes) && (p as Path2D).nodes.length >= 2

/** does a wall stand AT this level — its own level, or a taller wall rising through it
 *  (Santiago: a wall on Main that runs up past Upper hosts Upper's windows too) */
export function wallReachesLevel(
  model: ModelDoc, w: { levelId: LevelId; params?: Record<string, ParamValue> }, levelId: LevelId,
): boolean {
  if (w.levelId === levelId) return true
  const own = getLevel(model, w.levelId), target = getLevel(model, levelId)
  if (!own || !target) return false
  const p = w.params ?? {}
  const z0 = own.elevationFt + (typeof p.baseOffsetFt === 'number' ? p.baseOffsetFt : 0)
  const topLv = p.topMode === 'level' && typeof p.topLevelId === 'string' && p.topLevelId ? getLevel(model, p.topLevelId as LevelId) : null
  const z1 = topLv
    ? topLv.elevationFt + (typeof p.topOffsetFt === 'number' ? p.topOffsetFt : 0)
    : z0 + (typeof p.unconnectedHeightFt === 'number' ? p.unconnectedHeightFt : 9)
  // the target level's floor sits inside the wall with room above it for an opening
  return target.elevationFt >= z0 - 1e-6 && target.elevationFt < z1 - 1
}

/** the wall (of `category`) standing at `levelId` whose path passes within `tolFt` of
 *  `pt` → { id, s (station), dist }, nearest first; `excludeId` skips one wall */
export function hostAt(
  model: ModelDoc, category: string, levelId: LevelId, pt: Pt2, tolFt: number, excludeId?: ElementId,
): { id: ElementId; s: number; dist: number } | null {
  let best: { id: ElementId; s: number; dist: number } | null = null
  for (const w of listElements(model)) {
    if (w.category !== category || w.id === excludeId || !wallReachesLevel(model, w, levelId)) continue
    const path = w.params?.path
    if (!isPath(path)) continue
    const { s, dist } = projectToPolyline(flattenPathPts(path), pt)
    if (dist <= tolFt && (!best || dist < best.dist)) best = { id: w.id, s, dist }
  }
  return best
}

/** where a hosted element sits in plan (its station on its host's path) */
export function hostedPoint(model: ModelDoc, id: ElementId): Pt2 | null {
  const el = getElement(model, id)
  if (!el?.hostId) return null
  const host = getElement(model, el.hostId)
  const path = host?.params?.path
  if (!isPath(path)) return null
  const s = typeof el.params.hostOffsetFt === 'number' ? el.params.hostOffsetFt : 0
  return polylinePointAt(flattenPathPts(path), s).pt
}

const isOpening = (c: string | undefined) => c === 'door' || c === 'window'

export interface OpeningLevelResult {
  ok: boolean
  /** true when it moved into a different wall */
  rehosted: boolean
  /** when the level changed but no wall stands there at its spot */
  noHost?: boolean
}

/**
 * Change a door/window's level. Its wall still stands there (a tall wall) → just the
 * level. Otherwise → re-host into the wall at the same plan spot on the new level, in
 * the same undo step. No wall there → refused (left where it was), so an opening never
 * ends up floating above its wall cutting nothing.
 */
export function setOpeningLevel(model: ModelDoc, id: ElementId, levelId: LevelId): OpeningLevelResult {
  const el = getElement(model, id)
  const em = maps(model).elements.get(id)
  if (!el || !em) return { ok: false, rehosted: false }
  const host = el.hostId ? getElement(model, el.hostId) : null
  if (!isOpening(el.category) || !host || el.params.hostBId) {
    transact(model, () => { em.set('levelId', levelId) })
    return { ok: true, rehosted: false }
  }
  if (wallReachesLevel(model, host, levelId)) {
    transact(model, () => { em.set('levelId', levelId) })
    return { ok: true, rehosted: false }
  }
  const at = hostedPoint(model, id)
  const found = at ? hostAt(model, 'wall', levelId, at, 1.5) : null
  if (!found) return { ok: false, rehosted: false, noHost: true }
  transact(model, () => {
    em.set('levelId', levelId)
    em.set('hostId', found.id)
    ;(em.get('params') as Y.Map<unknown>).set('hostOffsetFt', found.s)
  })
  return { ok: true, rehosted: true }
}

/**
 * Revit's Pick New Host: the same door/window — type, size, sill, hand, facing,
 * hardware, every setting — moved into `hostId` at station `s`. Its level is
 * `levelId` when that wall stands at it (the level you're working on), else the new
 * wall's own level. Corner sliders (two hosts) are refused.
 */
export function pickNewHost(
  model: ModelDoc, id: ElementId, hostId: ElementId, s: number, levelId?: LevelId | null,
): { ok: boolean; reason?: string } {
  const el = getElement(model, id)
  const em = maps(model).elements.get(id)
  const host = getElement(model, hostId)
  if (!el || !em || !host) return { ok: false, reason: 'missing' }
  if (!isOpening(el.category)) return { ok: false, reason: 'Only doors and windows can pick a new host' }
  if (el.params.hostBId) return { ok: false, reason: 'A corner unit sits on two walls — move the walls instead' }
  if (el.params.pinned === true) return { ok: false, reason: 'It’s pinned — unpin it first' }
  // FENCES stage 3: a gate may move into a fence (and back into a wall)
  const isGate = el.category === 'door' && /^gate/.test(String(getType(model, el.typeId)?.params?.operation ?? ''))
  if (host.category === 'fence' && !isGate) return { ok: false, reason: 'Only gates go in a fence' }
  if (host.category !== 'wall' && host.category !== 'fence') return { ok: false, reason: 'Pick a wall' }
  const lv = host.category === 'fence' ? host.levelId : levelId && wallReachesLevel(model, host, levelId) ? levelId : host.levelId
  transact(model, () => {
    em.set('hostId', hostId)
    em.set('levelId', lv)
    ;(em.get('params') as Y.Map<unknown>).set('hostOffsetFt', s)
  })
  return { ok: true }
}

/**
 * STACKING OPENINGS (2026-10-01, Matt: "copy windows down on the exterior elevations so I
 * can stack them in the same wall"): put a door/window's sill at an ABSOLUTE height. The
 * level becomes the one whose floor is the highest at-or-below that sill — so copying a
 * window down past a floor line lands it on the story below with its sill measured from
 * that floor (it used to clamp at 0 on its own level). Its wall stays when the wall
 * stands at that level (a two-story wall); otherwise it moves into the wall at the same
 * plan spot on that level. No such wall → it stays on its own level, clamped at its floor.
 * A head-locked window switches to sill-set. Returns which level it landed on.
 */
export function placeOpeningAtZ(model: ModelDoc, id: ElementId, sillAbsZ: number): { levelId: LevelId; rehosted: boolean } | null {
  const el = getElement(model, id)
  const em = maps(model).elements.get(id)
  if (!el || !em || !isOpening(el.category)) return null
  const host = el.hostId ? getElement(model, el.hostId) : null
  const levels = [...listLevels(model)].sort((a, b) => a.elevationFt - b.elevationFt)
  let target = levels[0]
  for (const lv of levels) if (lv.elevationFt <= sillAbsZ + 1e-6) target = lv
  const pmOf = () => em.get('params') as Y.Map<unknown>
  const setSill = (lvElev: number) => {
    const pm = pmOf()
    pm.set('sillFt', Math.max(0, sillAbsZ - lvElev))
    if (pm.get('sillMode') === 'head') pm.set('sillMode', 'sill')
  }
  const own = getLevel(model, el.levelId)
  if (!target || !own || target.id === el.levelId || !host || el.params.hostBId) {
    transact(model, () => setSill(own?.elevationFt ?? 0))
    return { levelId: el.levelId, rehosted: false }
  }
  if (wallReachesLevel(model, host, target.id)) {
    transact(model, () => { em.set('levelId', target.id); setSill(target.elevationFt) })
    return { levelId: target.id, rehosted: false }
  }
  const at = hostedPoint(model, id)
  const found = at ? hostAt(model, 'wall', target.id, at, 1.5) : null
  if (!found) {
    transact(model, () => setSill(own.elevationFt))
    return { levelId: el.levelId, rehosted: false }
  }
  transact(model, () => {
    em.set('levelId', target.id)
    em.set('hostId', found.id)
    pmOf().set('hostOffsetFt', found.s)
    setSill(target.elevationFt)
  })
  return { levelId: target.id, rehosted: true }
}
